import cloneDeep from 'lodash/cloneDeep';
import joi, { Schema } from 'joi';
import { DefaultValues } from 'react-hook-form/dist/types/form';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useJoiResolver } from '../../../../shared/forms/use-joi-resolver';

interface TraitFormProps<T> {
  value: DefaultValues<T>;
  onChange: (value: T) => void;
  children: ReactNode;
  schema?: Schema;
}

export function TraitForm<T extends FieldValues>({
  value,
  onChange,
  schema = joi.any(),
  children,
}: TraitFormProps<T>) {
  const resolver = useJoiResolver(schema);

  const form = useForm<T>({
    defaultValues: value,
    mode: 'onChange',
    resolver,
  });

  const { formState, watch, reset } = form;
  const { isValid } = formState;
  const formValue = watch();

  useEffect(() => {
    reset(value);
  }, [value, reset]);

  // Ensures that isDirty doesn't go back to false when the form goes back in its initial state
  const [isDirty, setIsDirty] = useState(formState.isDirty);

  useEffect(() => {
    setIsDirty(prev => !!prev || formState.isDirty);
  }, [formState.isDirty]);

  // avoids calling onChange multiple times when the value has not really changed
  const currentValueRef = useRef<T | null>(null);

  useEffect(() => {
    // using JSON.stringify because the reference of formValue never changes
    const valueChanged = JSON.stringify(formValue) !== JSON.stringify(currentValueRef.current);
    if (isValid && isDirty && valueChanged) {
      /*
       * clone() is needed because react-hook-form mutates the value of formValue which causes currentValueRef.current
       * to always equal formValue.
       */
      currentValueRef.current = cloneDeep(formValue);
      onChange(formValue as any);
    }
  }, [isValid, isDirty, formValue, currentValueRef]);

  return <FormProvider {...form}>{children}</FormProvider>;
}
